import React, { useRef } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/headerlogo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdf = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

export default function TransportationTable4({ data }) {
  const pdfRef = useRef();

  if (!data || data.length === 0) {
    return <p>No data available</p>;
  }

  /* =============================
     PREPARE YEARS
  ============================== */

  const years = Object.keys(data[0])
    .filter((k) => !isNaN(k) && Number(k) <= 2024)
    .sort();

  /* =============================
     CALCULATIONS
  ============================== */

  const latestYear = years[years.length - 1];

  const latestValues = data.map((record) => ({
    geography: record.geography,
    value: Number(record[latestYear] || 0),
  }));

  const topGeography = latestValues.reduce((max, curr) =>
    curr.value > max.value ? curr : max,
  );

  const growthRates = data.map((record) => {
    const first = Number(record[years[0]] || 0);
    const last = Number(record[latestYear] || 0);
    const growth = first > 0 ? (((last - first) / first) * 100).toFixed(1) : 0;
    return {
      geography: record.geography,
      growth: Number(growth),
    };
  });

  const fastestGrowth = growthRates.reduce((max, curr) =>
    curr.growth > max.growth ? curr : max,
  );

  /* =============================
     CHART DATA
  ============================== */

  const chartData = {
    labels: years,
    datasets: data.map((record, idx) => ({
      label: record.geography,
      data: years.map((year) => Number(record[year] || 0)),
      borderColor: `hsl(${(idx * 90) % 360}, 70%, 45%)`,
      backgroundColor: `hsl(${(idx * 90) % 360}, 70%, 80%)`,
      fill: false,
      tension: 0.3,
      pointRadius: 4,
    })),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "📊 Transportation Employment by Geography (2018–2024)",
        font: { size: 16, weight: "bold" },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: ${context.parsed.y.toLocaleString(
              "en-US",
            )}`;
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Year" },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        title: { display: true, text: "Employment Count" },
        ticks: {
          callback: (value) => value.toLocaleString("en-US"),
        },
      },
    },
  };

  /* =============================
     PDF EXPORT
  ============================== */

  const downloadPDF = () => {
    const input = pdfRef.current;

    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdfDoc = new jsPDF("p", "mm", "a4");

      const pageWidth = pdfDoc.internal.pageSize.getWidth();
      const imgWidth = pageWidth - 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdfDoc.addImage(utahData, "PNG", 60, 10, 32, 16);
      pdfDoc.setFontSize(26);
      pdfDoc.text("UTahData.org", 95, 22);
      pdfDoc.line(10, 35, 200, 35);

      pdfDoc.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);

      pdfDoc.line(10, 260, 200, 260);
      pdfDoc.addImage(utahData, "PNG", 22, 265, 30, 15);
      pdfDoc.setFontSize(10);
      pdfDoc.text("Built by", 55, 275);
      pdfDoc.addImage(bodex, "PNG", 68, 269, 44, 10);
      pdfDoc.text("using", 112, 275);
      pdfDoc.addImage(kpi, "PNG", 125, 265, 20, 20);
      pdfDoc.text(
        `Downloaded on: ${new Date().toLocaleDateString()}`,
        150,
        295,
      );
      pdfDoc.text(`From: ${window.location.href}`, 10, 295);

      pdfDoc.save("transportation_geography_trend.pdf");
    });
  };

  /* =============================
     UI
  ============================== */

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-lg">
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Transportation Employment by Geography
        </h2>

        {/* SUMMARY CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Top Geography ({latestYear})
            </p>
            <p className="text-xl font-bold text-blue-800">
              {topGeography.geography}
            </p>
          </div>

          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Highest Employment
            </p>
            <p className="text-xl font-bold text-green-800">
              {topGeography.value.toLocaleString("en-US")}
            </p>
          </div>

          <div className="p-4 bg-yellow-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Fastest Growth (2018–2024)
            </p>
            <p className="text-xl font-bold text-yellow-800">
              {fastestGrowth.geography} (+{fastestGrowth.growth}%)
            </p>
          </div>
        </div>

        {/* TABLE + CHART */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* TABLE */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden text-sm">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-2">Geography</th>
                  {years.map((year) => (
                    <th key={year} className="p-2">
                      {year}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((record) => (
                  <tr
                    key={record.geography}
                    className={`${
                      record.geography === topGeography.geography
                        ? "bg-green-100 font-bold"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    <td className="p-2">{record.geography}</td>
                    {years.map((year) => (
                      <td key={year} className="p-2">
                        {Number(record[year] || 0).toLocaleString("en-US")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* CHART */}
          <div className="w-full h-96 bg-white rounded-lg shadow-md p-4">
            <Line data={chartData} options={options} />
          </div>
        </div>
      </div>

      {/* DOWNLOAD BUTTON */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
